import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Get playtester assignments for the authenticated user
 * @returns {Promise<Array>} Array of game assignments
 */
export async function getPlaytesterAssignments() {
  try {
    const token = getToken();
    if (!token) {
      console.error("No token available for playtester assignments request");
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/playtester/assignments`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });
    
    // Backend returns { assignments: [...] }, extract the array
    return response.data?.assignments || response.data || [];
  } catch (error) {
    if (error.response?.status === 403) {
      return [];
    }
    if (error.response?.status === 401) {
      return [];
    }
    if (error.response?.status === 404) {
      return [];
    }
    if (error.response?.status === 500) {
      return [];
    }
    return [];
  }
}

export async function getPublicPlaytests() {
  try {
    const token = getToken();
    if (!token) {
      return [];
    }

    const response = await axios.get(`${API_BASE_URL}/playtester/public-playtests`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    return response.data?.games || [];
  } catch (error) {
    console.error("Error fetching public playtests:", error);
    return [];
  }
}

/**
 * Get a secure download URL for a public playtest game build
 * @param {number|string} gameId - The game ID
 * @returns {Promise<string|null>} Download URL or null if unavailable
 */
export async function getPublicPlaytestDownloadUrl(gameId) {
  try {
    if (!gameId) {
      return null;
    }

    const response = await axios.get(
      `${API_BASE_URL}/playtester/public-playtests/${gameId}/download`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return (
      response.data?.downloadUrl ||
      response.data?.url ||
      response.data?.signedUrl ||
      null
    );
  } catch (error) {
    console.error("Error getting public playtest download URL:", error);
    return null;
  }
}

/**
 * Update assignment status
 * @param {number} assignmentId - The assignment ID
 * @param {string} status - New status ('assigned', 'in_progress', 'completed', 'cancelled')
 * @returns {Promise<Object|null>} Updated assignment or null if error
 */
export async function updateAssignmentStatus(assignmentId, status) {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/playtester/assignments/${assignmentId}/status`,
      { status },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating assignment status:", error);
    return null;
  }
}

/**
 * Get assignment details
 * @param {number} assignmentId - The assignment ID
 * @returns {Promise<Object|null>} Assignment details or null if error
 */
export async function getAssignmentDetails(assignmentId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/playtester/assignments/${assignmentId}`, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching assignment details:", error);
    return null;
  }
}

/**
 * Get a secure download URL for a file-based assignment
 * @param {number|string} assignmentId - The assignment ID
 * @returns {Promise<string|null>} Download URL or null if unavailable
 */
export async function getAssignmentDownloadUrl(assignmentId) {
  try {
    if (!assignmentId) {
      return null;
    }

    const response = await axios.get(
      `${API_BASE_URL}/playtester/assignments/${assignmentId}/download`,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return (
      response.data?.downloadUrl ||
      response.data?.url ||
      response.data?.signedUrl ||
      null
    );
  } catch (error) {
    console.error("Error getting assignment download URL:", error);
    return null;
  }
}

